import { storage } from '@abyss/web/tools/storage';
import { getEnglishModule } from '@uhg-psx/mapping-files-translation-transperfect';
import { MutableRefObject } from 'react';

import {
  CareCategories,
  Constants,
  MemberCoverageTypeToUICoverageTypeConversion,
  ReverseCoverageTypesCodes,
  SectionType,
  TypeaheadChipCategoriesMapping,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { handleSearchBarSearchButtonClickOrEnter } from '../../common/PSXHeader/SearchBar/utils';
import { mappingRollUpCodeToCategory } from '../../common/RecentActivity/helper';
import { getFeatureFlag } from '../../common/Utils';
import {
  isLawwMember,
  isSupportFinderMember,
} from '../../common/Utils/memberUtils/memberUtils';
import { ChipCategory } from '../../frontends/ProviderSearch/routes/SearchResults/categoryCountTypes';
import {
  getCorrectChipCategory,
  getSearchResultsPageParams,
  getSearchType,
  getSelectedCoverageType,
} from '../../frontends/ProviderSearch/routes/SearchResults/SearchResults.UtilsV2';
import {
  getProviderSearchPayload,
  getProviderSearchPayloadWithRecriprocityId,
} from '../../frontends/ProviderSearch/routes/SearchResults/searchUtils';
import { UseParallelSearchResults } from '../../frontends/ProviderSearch/routes/SearchResults/useParallelSearchResultsCategoriesCount';
import { getFirstEnabledChip } from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import { TypeaheadSearchState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  BrowseAllVariant,
  getProviderTypeBySectionType,
  handleOnCommonlySearchedClick,
  handleOnSpecialtyOrFacilityClick,
  handleSearchBarSpecialtySuggestionClickOrEnter,
} from '../../utils/typeahead.utils';
import { getOnlineRetailers } from '../../utils/user.utils';

export const getEnabledChipCategories = (specialityResults: ChipCategory[]) =>
  specialityResults.reduce<string[]>((acc, item) => {
    if (item.chipEnabled) {
      acc.push(TypeaheadChipCategoriesMapping[item.chipCategory]);
    }
    return acc;
  }, []);

export const getChipToSelect = ({
  specialtySearch,
  enabledChipForSpecialitySearch,
  enabledChipForNameSearch,
  navigateToChipCategory,
  chipValueFromParams,
  availableCategoryChipsForSpecialtySearch,
  availableCategoryChipsForNameSearch,
}) => {
  const isNameSearch = enabledChipForNameSearch && !specialtySearch;
  if (isNameSearch) {
    if (
      chipValueFromParams &&
      availableCategoryChipsForNameSearch.includes(chipValueFromParams)
    ) {
      return chipValueFromParams;
    }
    return enabledChipForNameSearch;
  } else {
    if (
      chipValueFromParams &&
      availableCategoryChipsForSpecialtySearch.includes(chipValueFromParams)
    ) {
      return chipValueFromParams;
    }
    if (
      navigateToChipCategory &&
      availableCategoryChipsForSpecialtySearch.includes(navigateToChipCategory)
    ) {
      return navigateToChipCategory;
    }
    return enabledChipForSpecialitySearch;
  }
};

export interface OnCategoryCountLoadedParams {
  categoryCountResults: UseParallelSearchResults;
  typeAheadData: TypeaheadSearchState;
  chipValue: string;
  searchParamsObject: any;
  isSpecialtySearch: boolean;
  enableNavigateToCorrectCategory: boolean;
  typeaheadPreferenceCodes: string[];
  dataRef: MutableRefObject<any>;
  previousDataRef: MutableRefObject<any>;
  isEmptyResults: MutableRefObject<boolean>;
  setIsLoading: (value: boolean) => void;
  setChipValue: (value: string) => void;
  setCoverageType: (value: string) => void;
  setSearchParams: (params: any, options?: any) => void;
}

// This is to load the initial Provider Search Results after Category Count is loaded
export const onCategoryCountLoaded = ({
  categoryCountResults,
  typeAheadData,
  chipValue,
  searchParamsObject,
  isSpecialtySearch,
  enableNavigateToCorrectCategory,
  typeaheadPreferenceCodes,
  dataRef,
  previousDataRef,
  isEmptyResults,
  setIsLoading,
  setChipValue,
  setCoverageType,
  setSearchParams,
}: OnCategoryCountLoadedParams) => {
  const { chipValue: chipValueFromParams } = searchParamsObject;
  const { combinedIncludeSpecialityRollupCodes = '' } = typeAheadData;
  const { nameResults, specialityResults } = categoryCountResults;
  const enabledChipForNameSearch = getFirstEnabledChip(nameResults);
  const enabledChipForSpecialitySearch = getFirstEnabledChip(specialityResults);
  const hasOnlySpecialitySearchEnabled =
    !enabledChipForNameSearch && !!enabledChipForSpecialitySearch;

  const availableCategoryChipsForSpecialtySearch =
    getEnabledChipCategories(specialityResults);
  const availableCategoryChipsForNameSearch =
    getEnabledChipCategories(nameResults);

  const navigateToChipCategory = enableNavigateToCorrectCategory
    ? getCorrectChipCategory(
        combinedIncludeSpecialityRollupCodes,
        typeaheadPreferenceCodes,
        specialityResults
      )
    : '';

  const specialtySearch = hasOnlySpecialitySearchEnabled || isSpecialtySearch;
  const chipToSelect = getChipToSelect({
    specialtySearch,
    enabledChipForSpecialitySearch,
    enabledChipForNameSearch,
    navigateToChipCategory,
    chipValueFromParams,
    availableCategoryChipsForSpecialtySearch,
    availableCategoryChipsForNameSearch,
  });

  // If chipToSelect is defined, then we have results for that category
  if (chipToSelect) {
    const selectedCoverageType = getSelectedCoverageType(chipToSelect);
    setChipValue(chipToSelect);
    setCoverageType(ReverseCoverageTypesCodes[selectedCoverageType]);
    setSearchParams(
      () => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('chipValue', chipToSelect);
        searchParams.set(
          'coverageType',
          ReverseCoverageTypesCodes[selectedCoverageType]
        );
        searchParams.set('searchType', getSearchType(specialtySearch));
        return searchParams;
      },
      { replace: true }
    );
    // We need this setTimeout for below reasons.
    // Say that user is in NSR page, and trigers a new search, Apollo does not trigger onCompleted
    // when the new results is the same as the previous results (prior to NSR page).
    // So, we are setting the isLoading to false after 1 second if the data is the same as the previous data
    setTimeout(() => {
      if (dataRef?.current === previousDataRef.current) {
        setIsLoading?.(false);
      }
      previousDataRef.current = null;
    }, 1000);
  } else {
    // chipToSelect is not defined, so we don't have results for any category
    // So, we need to show the empty results page
    isEmptyResults.current = true;
    previousDataRef.current = dataRef?.current || null;
    setChipValue(chipValueFromParams);
    const { coverageType } = typeAheadData;
    if (coverageType && chipValue === CareCategories.ALL) {
      setCoverageType(coverageType);
      setSearchParams(
        (searchParams) => {
          searchParams.set('coverageType', coverageType);
          return searchParams;
        },
        { replace: true }
      );
    }
    setIsLoading?.(false);
  }
  return { chipToSelect, specialtySearch };
};

export const getDataForProviders = ({
  search,
  uniqueSearch,
  isSpecialtySearch,
  chipToUse,
  baseParams,
  typeAheadData,
  choosePCP,
  sectionType,
  categoryCountResultsRef,
  setTypeaheadSearchStore,
  getProviderSearchResults,
  memberCoverageTypes,
  featureFlags,
  currentMember,
  coverageType,
  onlineRetailers,
  enableBHAoeTypeahead,
  enableAoeMedical,
  setShowFacilities,
  setShowProviderGroups,
  setShowProviders,
  cleanUpProviderCalls,
  setShowVirtualResultsOnly,
}) => {
  const uesTranslation = getEnglishModule('uesSpecialityTranslations');
  const {
    providerSearchParams,
    otherParams: { coverageTypes, portalData },
  } = getSearchResultsPageParams({
    ...baseParams,
    typeAheadData,
    chipValue: chipToUse,
    uniqueSearch,
  });

  const {
    includeSpecialityRollupCodes,
    pseudoVerticalsDetails,
    isUserTextSearch,
    typeaheadAoeCodes,
    keyword,
    bhProgramFacilityAgg,
  } = providerSearchParams;

  const isLAWWOrSupportFinderDeeplink = resolveIsLAWWOrSupportFinderDeeplink(
    portalData,
    providerSearchParams
  );

  if (!(choosePCP || search || isLAWWOrSupportFinderDeeplink)) {
    return;
  }
  const searchTypeToUse = getSearchType(isSpecialtySearch);
  const payloadWithRecriprocityId = getProviderSearchPayloadWithRecriprocityId(
    memberCoverageTypes,
    featureFlags,
    currentMember,
    coverageType,
    choosePCP,
    onlineRetailers
  );
  const payload = getProviderSearchPayload(
    payloadWithRecriprocityId,
    isSpecialtySearch,
    searchTypeToUse,
    isUserTextSearch,
    typeaheadAoeCodes,
    enableBHAoeTypeahead,
    includeSpecialityRollupCodes,
    pseudoVerticalsDetails?.isPseudoRollupSearch,
    uesTranslation,
    search,
    keyword,
    coverageTypes,
    bhProgramFacilityAgg,
    enableAoeMedical
  );

  const selectedCategory = Constants.CARE_CATEGORIES_TYPEAHEAD.find(
    (category) => category.label === chipToUse
  );
  const selectedCategoryPayload = payload.find(
    (item) => item.chipCategory === selectedCategory?.id
  );

  const enableCategoryCountV2 = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CATEGORY_COUNT_V2
  );
  const { specialityResults } = categoryCountResultsRef.current;
  const providerQueryTypesFiltered: any =
    specialityResults?.filter(
      (item) => item.chipCategory === selectedCategory?.id
    )?.[0] || [];
  const providerQueryTypes = enableCategoryCountV2
    ? providerQueryTypesFiltered.providerTypeList
    : providerQueryTypesFiltered.providerTypes;
  setTypeaheadSearchStore({ providerQueryTypes });
  getProviderSearchResults({
    setShowFacilities,
    setShowProviderGroups,
    setShowProviders,
    providerSearchParams: {
      ...providerSearchParams,
      searchType: searchTypeToUse,
      search,
    },
    choosePCP,
    cleanUpProviderCalls,
    pseudoVerticalsDetails,
    selectedCategory,
    selectedCategoryPayload,
    specialtySearch: isSpecialtySearch,
    searchType: searchTypeToUse,
    providerQueryTypes,
    ...(sectionType
      ? {
          providerType: getProviderTypeBySectionType(
            sectionType as SectionType
          ),
        }
      : {}),
    setShowVirtualResultsOnly,
  });
};

export const retreiveTypeAheadData = ({
  enableRetriveTypeAheadData,
  resolvedSource,
  psxSource,
  search,
  searchType,
  searchParams,
  commonSearchesLagoon,
  specialityPreferenceTable,
  visionCare,
  searchTerm,
  getCoverageType,
  dependentSeqNbr,
  choosePCP,
  chipStore,
  setTypeaheadSearchStore,
  setAnalyticsState,
  setPCPSearchState,
  setChipValue,
  setCoverageType,
  setDetailsStore,
  setSearchStore,
  facilities,
  specialties,
  currentMember,
  GetAutoCompleteResults,
  onTypeAheadDataLoaded,
  typeaheadSearchStore,
  sourceFromGlobalSearch,
}) => {
  try {
    const isGlobalSearch =
      sourceFromGlobalSearch === Constants.SEARCH_SOURCE_TYPES.GLOBAL_SEARCH;
    const isCommonSearch =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.COMMON_SEARCHES;
    const isBrowseForCareSearch =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.BROWSE_FOR_CARE;
    const isSpecialtyDrawerSearch =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.SPECIALTY_DRAWER;
    const isFacilitiesDrawerSearch =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.FACILITIES_DRAWER;
    const isRecentsearches =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.RECENT_SEARCHES;
    const isAutoCompleteSearch =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.AUTO_COMPLETE;
    const isDeeplinkSearch =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.DEEPLINK;
    const isSpecialtyCategorySearch =
      resolvedSource === Constants.SEARCH_SOURCE_TYPES.SPECIALTY_CATEGORY;

    if (isSpecialtyCategorySearch) {
      const specialtyCategory = searchParams.get('specialtyCategory');

      const specialtyCode =
        visionCare.find(
          ({ DNT_heading }) =>
            DNT_heading.toLowerCase() === specialtyCategory?.toLowerCase()
        )?.specialtyCode || '';

      // If the specialty code is found in the visionCare table, it belongs to the External Vision Care flow for CNS
      if (specialtyCode) {
        const typeaheadValues = {
          combinedIncludeSpecialityRollupCodes: specialtyCode,
          includeSpecialityRollupCodes: specialtyCode,
          search,
          searchType,
        };
        onTypeAheadDataLoaded(typeaheadValues);
      } else {
        onTypeAheadDataLoaded(typeaheadSearchStore);
      }
    } else if (isRecentsearches) {
      const recentSearchesKey = `${search}:${psxSource}`;
      const typeaheadValues =
        storage.session.get('recent-searches')?.[recentSearchesKey];
      if (typeaheadValues) {
        setTypeaheadSearchStore(typeaheadValues);
        // if we have the data in session storage use them or else use auto complete query
        onTypeAheadDataLoaded(typeaheadValues);
      } else {
        GetAutoCompleteResults({
          query: encodeURIComponent(searchTerm.toString().trim()),
        });
      }
    } else if (isCommonSearch) {
      const value = commonSearchesLagoon.find(
        ({ psxKeyword }) => search === psxKeyword
      );
      if (!value) {
        onTypeAheadDataLoaded();
        return;
      }
      const typeaheadValues = handleSearchBarSpecialtySuggestionClickOrEnter({
        enableRetriveTypeAheadData,
        specialityPreferenceTable,
        value,
        searchTerm,
        commonSearch: true,
        getCoverageType,
        dependentSeqNbr,
        choosePCP,
        chipStore,
        setTypeaheadSearchStore,
        setAnalyticsState,
        setPCPSearchState,
        setChipValue,
        setCoverageType,
        setDetailsStore,
        currentMember,
        navigate: undefined,
      });
      onTypeAheadDataLoaded(typeaheadValues);
    } else if (isBrowseForCareSearch) {
      const value = commonSearchesLagoon.find(
        ({ psxKeyword }) => search === psxKeyword
      );
      if (!value) {
        onTypeAheadDataLoaded();
        return;
      }
      const typeaheadValues = handleOnCommonlySearchedClick({
        item: value,
        psxSource,
        navigate: undefined,
        chipStore,
        setAnalyticsState,
        setPCPSearchState,
        setTypeaheadSearchStore,
        setSearchStore,
        getOnlineRetailers,
        mappingRollUpCodeToCategory,
        enableRetriveTypeAheadData,
      });
      onTypeAheadDataLoaded(typeaheadValues);
    } else if (isAutoCompleteSearch || isGlobalSearch || isDeeplinkSearch) {
      GetAutoCompleteResults({
        query: encodeURIComponent(searchTerm.toString().trim()),
      });
    } else if (isFacilitiesDrawerSearch || isSpecialtyDrawerSearch) {
      const records = isFacilitiesDrawerSearch ? facilities : specialties;
      if (!records.length) {
        return;
      }
      const item = records.find(({ name }) => name === search);
      if (!item) {
        onTypeAheadDataLoaded();
        return;
      }
      const typeaheadValues = handleOnSpecialtyOrFacilityClick({
        item,
        navigate: undefined,
        setTypeaheadSearchStore,
        setAnalyticsState,
        chipState: chipStore,
        setDetailsStore,
        variant: isFacilitiesDrawerSearch
          ? BrowseAllVariant.Facility
          : BrowseAllVariant.Specialty,
        enableRetriveTypeAheadData,
      });
      onTypeAheadDataLoaded(typeaheadValues);
    }
  } catch {
    onTypeAheadDataLoaded(typeaheadSearchStore);
  }
};

export const retriveTypeAheadDataFromAutoComplete = ({
  suggestions,
  typeAheadSuggestions,
  combinedRollupCodes,
  aggregatedAoeCodes,
  bhProgramFacilityAgg,
  search,
  searchLinkName,
  searchTypeFromParams,
  searchParams,
  enableRetriveTypeAheadData,
  enableUESSuggestionMatch,
  specialityPreferenceTable,
  getCoverageType,
  dependentSeqNbr,
  choosePCP,
  chipStore,
  setTypeaheadSearchStore,
  setAnalyticsState,
  setPCPSearchState,
  setChipValue,
  setCoverageType,
  setDetailsStore,
  currentMember,
  convertTypeaheadProviderIdAndType,
  getTypeAheadCategory,
  onTypeAheadDataLoaded,
}) => {
  try {
    const value = suggestions?.find(
      ({ suggestion }) =>
        suggestion?.toLowerCase() === search.toString().toLowerCase()
    );

    const { isSearchButtonClickOrKeyboardEnter, isSuggestionClickOrEnter } =
      getSearchInfo(searchParams);

    if (isSuggestionClickOrEnter) {
      if (!value) {
        onTypeAheadDataLoaded();
        return;
      }
      const typeaheadValues = handleSearchBarSpecialtySuggestionClickOrEnter({
        enableRetriveTypeAheadData,
        specialityPreferenceTable,
        value,
        searchTerm: search,
        commonSearch: false,
        getCoverageType,
        dependentSeqNbr,
        choosePCP,
        chipStore,
        setTypeaheadSearchStore,
        setAnalyticsState,
        setPCPSearchState,
        setChipValue,
        setCoverageType,
        setDetailsStore,
        currentMember,
        navigate: undefined,
      });
      onTypeAheadDataLoaded(typeaheadValues);
    } else if (isSearchButtonClickOrKeyboardEnter) {
      const analyticsLinkName = searchLinkName || 'search button';
      const typeaheadLinkName =
        searchLinkName === 'general search'
          ? 'typeahead general search'
          : 'typeahead search';

      const typeaheadValues = handleSearchBarSearchButtonClickOrEnter(
        {
          isLLMGeneralSearchEnabled: searchLinkName === 'general search',
          enableRetriveTypeAheadData,
          enableUESSuggestionMatch,
          typeAheadSuggestions: typeAheadSuggestions || [],
          navigate: undefined,
          headers: undefined,
          analyticsLinkName,
          typeaheadLinkName,
          currentMember,
          userSearchTerm: search,
          combinedRollupCodes,
          aggregatedAoeCodes,
          bhProgramFacilityAgg,
          setIsFocusedOnKeywordSearchInput: () => {},
          convertTypeaheadProviderIdAndType,
          getTypeAheadCategory,
          store: {
            chipStore,
            setTypeaheadSearchStore,
            setAnalyticsState,
            setPCPSearchState,
            setChipValue,
            setCoverageType,
            setDetailsStore,
          },
        },
        specialityPreferenceTable
      );

      if (typeaheadValues.isMixedSuggestions && searchTypeFromParams) {
        typeaheadValues.searchType = searchTypeFromParams;
      }
      onTypeAheadDataLoaded(typeaheadValues);
    }
  } catch {
    onTypeAheadDataLoaded();
  }
};

export const getConvertedCoverageArray = (memberCoverages) => {
  const convertedMemberCoverages = memberCoverages?.map(
    (cov) => MemberCoverageTypeToUICoverageTypeConversion[cov]
  );
  const dentalCoverage = convertedMemberCoverages?.find((cov) => cov === 'D');
  const visionCoverage = convertedMemberCoverages?.find((cov) => cov === 'V');
  const behavioralCoverage = convertedMemberCoverages?.find(
    (cov) => cov === 'B'
  );
  const medicalCoverage = convertedMemberCoverages?.find((cov) => cov === 'M');
  return [dentalCoverage, visionCoverage, medicalCoverage, behavioralCoverage];
};

export const getSearchTypeFromParams = ({
  searchType,
  condition,
  specialtyCategory,
  gender,
  virtualCare,
  acceptsEap,
}: Record<string, string>) => {
  if (searchType) {
    return searchType;
  }
  if (condition || specialtyCategory || gender || virtualCare || acceptsEap) {
    return Constants.SEARCH_TYPES.SPECIALTY;
  }
  return searchType;
};

export const getSearchInfo = (searchParams: URLSearchParams) => {
  const searchParamsObject = Object.fromEntries(searchParams.entries());
  const { search, [Constants.PSX_SOURCE]: psxSource } = searchParamsObject;
  const searchType = getSearchTypeFromParams(searchParamsObject);
  const searchLinkName = psxSource?.split(':')?.[1];
  const isSearchButtonClickOrKeyboardEnter =
    ['search button', 'search enter', 'general search'].includes(
      searchLinkName
    ) || searchType === Constants.SEARCH_TYPES.NAME;
  const isSuggestionClickOrEnter = !isSearchButtonClickOrKeyboardEnter;

  return {
    search,
    searchType,
    isSuggestionClickOrEnter,
    isSearchButtonClickOrKeyboardEnter,
  };
};

export const resolveIsLAWWOrSupportFinderDeeplink = (
  { portalName }: { portalName: string },
  {
    gender,
    virtualCare,
    acceptsEap,
    specialtyCategory,
    condition,
  }: Record<string, string>
) => {
  const isLaww = isLawwMember(portalName);
  const isSupportFinder = isSupportFinderMember(portalName);
  if (!(isLaww || isSupportFinder)) {
    return false;
  } else if (
    gender ||
    virtualCare ||
    acceptsEap ||
    specialtyCategory ||
    condition
  ) {
    return true;
  }
  return false;
};

export const resolvedCombinedSearchResults = ({
  combinedSearchResults,
  uniqueSearch,
  showProviders,
  showFacilities,
  showProviderGroups,
}) => {
  const {
    totalCountPractitioners,
    totalCountOrganizations,
    totalCountMedicalGroup,
    uniqueSearchPractitioners,
    uniqueSearchOrganizations,
    uniqueSearchMedicalGroup,
  } = combinedSearchResults || {};

  const isLoaded = (
    showFlag: boolean,
    total: number | undefined,
    uniqueSearchFromResponse: string
  ) =>
    !showFlag ||
    (total !== undefined && uniqueSearch === uniqueSearchFromResponse);

  const isResultsLoaded =
    isLoaded(
      showProviders,
      totalCountPractitioners,
      uniqueSearchPractitioners
    ) &&
    isLoaded(
      showFacilities,
      totalCountOrganizations,
      uniqueSearchOrganizations
    ) &&
    isLoaded(
      showProviderGroups,
      totalCountMedicalGroup,
      uniqueSearchMedicalGroup
    );

  const hasResults = Boolean(
    (showProviders && totalCountPractitioners) ||
      (showFacilities && totalCountOrganizations) ||
      (showProviderGroups && totalCountMedicalGroup)
  );

  return { isResultsLoaded, hasResults };
};

/**
 * Removes leading and trailing slashes from a path string.
 */
export const trimSlashes = (path: string): string =>
  path.replace(/^\/+|\/+$/g, '');
